import React, { useState } from "react";
import {
  FaAppleAlt,
  FaLeaf,
  FaFish,
  FaGlassWhiskey,
  FaIceCream,
  FaSeedling,
  FaHeart,
  FaTimes,
  FaPlay,
  FaUtensils,
  FaWater,
  FaHome,
} from "react-icons/fa";
import { GiMeal, GiFruitBowl } from "react-icons/gi";
import { Link } from "react-router-dom";

import pregNutrition from "../assets/images/pregnancy-nutrition.jpg";
import periodNutrition from "../assets/images/period-nutrition.jpg";
import ironFoods from "../assets/images/iron-foods.png";
import omega3Foods from "../assets/images/omega3-foods.jpg";
import pregnancyMeal from "../assets/images/pregnancy-meal.jpg";
import periodMeal from "../assets/images/period-meal.webp";
import { FiHome } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

const pregnancyVideo =
  "https://www.youtube.com/embed/0BrxCY89_uQ?si=2IpwBF73Xd8K2Dfx";
const periodVideo =
  "https://www.youtube.com/embed/1SD_29gnFu0?si=rhOBjsQ-73Divlan";

const NutritionTips = () => {
  const [activeTab, setActiveTab] = useState("pregnancy");
  const [expandedTip, setExpandedTip] = useState(null);

  const nutritionData = {
    pregnancy: {
      title: "Pregnancy Nutrition Guide",
      image: pregNutrition,
      video: pregnancyVideo,
      description:
        "Optimal nutrition during pregnancy supports fetal brain development, organ formation, and maternal health. These dietary adjustments help meet increased nutritional demands.",
      keyBenefits: [
        "Supports placenta development",
        "Reduces risk of birth defects",
        "Manages pregnancy symptoms",
        "Prepares body for lactation",
      ],
      essentials: [
        {
          icon: <FaAppleAlt className="text-green-500 text-2xl" />,
          title: "Fruits & Vegetables",
          image: ironFoods,
          tips: [
            "5+ colorful servings daily (1 serving = 1 cup raw or ½ cup cooked)",
            "Dark leafy greens (spinach, kale) provide folate and iron",
            "Citrus fruits and berries for vitamin C to enhance iron absorption",
            "Sweet potatoes for beta-carotene (converts to vitamin A)",
          ],
        },
        {
          icon: <FaFish className="text-blue-500 text-2xl" />,
          title: "Proteins & Omega-3s",
          image: omega3Foods,
          tips: [
            "60-80g protein daily (lean meats, poultry, fish, eggs)",
            "2-3 servings fatty fish weekly (salmon, sardines) for DHA",
            "Plant proteins: lentils (1 cup = 18g protein), chickpeas, quinoa",
            "Nuts and seeds for healthy fats and magnesium",
          ],
        },
        {
          icon: <GiMeal className="text-yellow-500 text-2xl" />,
          title: "Meal Planning",
          image: pregnancyMeal,
          tips: [
            "Sample breakfast: Greek yogurt + berries + granola + flaxseeds",
            "Lunch: Grilled salmon salad with leafy greens and avocado",
            "Snacks: Hard-boiled eggs, cheese cubes, hummus with veggies",
            "Dinner: Lean protein + quinoa + roasted vegetables",
          ],
        },
      ],
      hydration: {
        icon: <FaWater className="text-blue-300 text-2xl" />,
        tips: [
          "10 cups (2.3L) fluids daily - includes herbal teas, soups",
          "Infuse water with lemon/cucumber for nausea relief",
          "Limit caffeine to <200mg/day (1-2 small coffees)",
          "Watch for dehydration signs: dark urine, dizziness",
        ],
      },
      supplements: [
        "Prenatal vitamin with 400-800mcg folic acid",
        "DHA supplement (200-300mg) if not eating fish",
        "Iron supplement if anemic (doctor recommended)",
        "Vitamin D 600IU daily",
      ],
    },
    period: {
      title: "Menstrual Cycle Nutrition",
      image: periodNutrition,
      video: periodVideo,
      description:
        "Tailoring nutrition to menstrual cycle phases can help manage energy, cravings, and symptoms. The 28-day cycle has distinct nutritional needs at each stage.",
      cyclePhases: [
        {
          name: "Menstrual (Days 1-5)",
          focus: "Iron-rich foods, anti-inflammatory nutrients",
        },
        {
          name: "Follicular (Days 6-14)",
          focus: "Energy-supporting complex carbs and proteins",
        },
        {
          name: "Ovulation (Days 15-17)",
          focus: "Fiber-rich foods to support estrogen metabolism",
        },
        {
          name: "Luteal (Days 18-28)",
          focus: "Magnesium and B vitamins for PMS symptoms",
        },
      ],
      essentials: [
        {
          icon: <FaLeaf className="text-green-500 text-2xl" />,
          title: "Iron Replenishment",
          image: ironFoods,
          tips: [
            "Heme iron sources (more absorbable): red meat, organ meats",
            "Plant-based iron: lentils (1 cup = 6.6mg), spinach, tofu",
            "Pair with vitamin C foods (bell peppers, citrus) to enhance absorption",
            "Avoid calcium supplements with iron-rich meals",
          ],
        },
        {
          icon: <FaIceCream className="text-pink-500 text-2xl" />,
          title: "Cravings Management",
          image: omega3Foods,
          tips: [
            "Dark chocolate (70%+) for magnesium and antioxidants",
            "Frozen banana 'nice cream' for sweet cravings",
            "Roasted chickpeas for salty cravings",
            "Nut butters on whole grain toast for fat cravings",
          ],
        },
        {
          icon: <GiFruitBowl className="text-purple-500 text-2xl" />,
          title: "Cycle-Specific Meals",
          image: periodMeal,
          tips: [
            "Menstrual phase: Beef stew with potatoes and carrots",
            "Ovulation: Salmon with quinoa and roasted vegetables",
            "Luteal phase: Magnesium-rich foods like almonds, spinach",
            "General: 3 balanced meals + 2 snacks to stabilize blood sugar",
          ],
        },
      ],
      hydration: {
        icon: <FaWater className="text-blue-300 text-2xl" />,
        tips: [
          "8-10 cups daily (more if heavy bleeding)",
          "Herbal teas: ginger for cramps, peppermint for bloating",
          "Coconut water for electrolytes during bleeding days",
          "Limit alcohol which can worsen bloating and mood swings",
        ],
      },
      supplements: [
        "Magnesium glycinate 300-400mg for cramps",
        "Omega-3s (EPA/DHA) for inflammation",
        "Vitamin B complex for energy metabolism",
        "Iron supplement if heavy bleeding (doctor recommended)",
      ],
    },
  };

  const toggleTip = (index) => {
    setExpandedTip(expandedTip === index ? null : index);
  };

  return (
    <div className="w-full font-nunito min-h-screen bg-white py-8 px-4 sm:px-6 lg:px-8">
  <Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Women's Nutrition Guide - FlowHearts | Pregnancy & Cycle Nutrition</title>
    <meta
      name="description"
      content="Explore FlowHearts' Women's Nutrition Guide for tailored dietary tips on pregnancy and menstrual cycle health to support hormone balance and wellness."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/nutrition-tips" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/nutrition-tips" />
    <meta
      property="og:title"
      content="Women's Nutrition Guide - FlowHearts | Pregnancy & Cycle Nutrition"
    />
    <meta
      property="og:description"
      content="Discover FlowHearts' science-backed nutrition guide for pregnancy and menstrual cycles, with meal plans and tips for hormonal health."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/nutrition-tips-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/nutrition-tips" />
    <meta
      property="twitter:title"
      content="Women's Nutrition Guide - FlowHearts | Pregnancy & Cycle Nutrition"
    />
    <meta
      property="twitter:description"
      content="Get tailored nutrition tips for pregnancy and menstrual cycles from FlowHearts, with science-backed meal plans for hormone balance."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/nutrition-tips-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Women's Nutrition Guide",
          "url": "https://flowhearts.com/nutrition-tips",
          "description": "FlowHearts' Women's Nutrition Guide offers tailored dietary tips for pregnancy and menstrual cycle health to support hormone balance.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto">
        <div className="flex justify-start mb-6">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200"
          >
            <FiHome className="text-pink-500" />
            <span className="text-purple-700 font-medium">Back to Home</span>
          </Link>
        </div>
        <div className="relative mb-8">
          <header className="text-center">
            <h2 className="text-4xl font-cormorant font-bold text-purple-800 mb-2">
              Women's Nutrition Guide
            </h2>
            <p className="text-xl text-pink-600">
              Science-backed dietary strategies for pregnancy and menstrual health
            </p>
          </header>
        </div>
        <div className="flex border-b border-purple-100 mb-8">
          <button
            onClick={() => setActiveTab("pregnancy")}
            className={`px-6 py-3 font-medium flex items-center ${
              activeTab === "pregnancy"
                ? "text-pink-600 border-b-2 border-pink-500"
                : "text-purple-700 hover:text-pink-500"
            }`}
          >
            <FaAppleAlt className="mr-2" />
            Pregnancy Nutrition
          </button>
          <button
            onClick={() => setActiveTab("period")}
            className={`px-6 py-3 font-medium flex items-center ${
              activeTab === "period"
                ? "text-pink-600 border-b-2 border-pink-500"
                : "text-purple-700 hover:text-pink-500"
            }`}
          >
            <FaLeaf className="mr-2" />
            Cycle Nutrition
          </button>
        </div>
        <div className="mb-10 bg-purple-50 rounded-xl overflow-hidden">
          <div className="aspect-w-16 aspect-h-9">
            <iframe
              src={nutritionData[activeTab].video}
              className="w-full h-96"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={nutritionData[activeTab].title}
            ></iframe>
          </div>
          <div className="p-4 bg-gradient-to-r from-purple-100 to-pink-100">
            <h3 className="font-semibold text-purple-800">
              {activeTab === "pregnancy"
                ? "Pregnancy Diet Explained"
                : "Eating For Your Cycle"}
            </h3>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-8 mb-10">
          <div className="lg:w-2/5">
            <div className="sticky top-4">
              <img
                src={nutritionData[activeTab].image}
                alt={nutritionData[activeTab].title}
                className="w-full h-auto rounded-xl shadow-lg border border-purple-200 mb-6"
              />
              <div className="bg-white p-5 rounded-xl border border-purple-100 shadow-sm">
                <h3 className="text-xl font-bold text-purple-800 mb-3">
                  Key Benefits
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-purple-700">
                  {activeTab === "pregnancy"
                    ? nutritionData.pregnancy.keyBenefits.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))
                    : nutritionData.period.cyclePhases.map((phase, i) => (
                        <li key={i}>
                          <span className="font-medium">{phase.name}:</span>{" "}
                          {phase.focus}
                        </li>
                      ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="lg:w-3/5 space-y-8">
            <div className="bg-white p-5 rounded-xl border border-purple-100 shadow-sm">
              <p className="text-purple-700">
                {nutritionData[activeTab].description}
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-purple-800 mb-5 border-b border-purple-100 pb-2">
                Essential Nutrition Guidelines
              </h2>
              {nutritionData[activeTab].essentials.map((item, index) => (
                <div
                  key={index}
                  className="mb-6 bg-white rounded-xl border border-purple-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleTip(index)}
                    className="w-full p-5 flex items-center justify-between text-left"
                  >
                    <div className="flex items-center">
                      <div className="mr-4">{item.icon}</div>
                      <h3 className="text-xl font-semibold text-purple-800">
                        {item.title}
                      </h3>
                    </div>
                    <svg
                      className={`w-5 h-5 text-purple-600 transform transition-transform ${
                        expandedTip === index ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  {expandedTip === index && (
                    <div className="p-5 pt-0 border-t border-purple-100">
                      <div className="flex flex-col md:flex-row gap-5 mb-4">
                        <img
                          src={item.image}
                          alt={item.title}
                          className="md:w-1/3 h-40 object-cover rounded-lg"
                        />
                        <ul className="md:w-2/3 list-disc pl-5 space-y-2 text-purple-700">
                          {item.tips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
              <h3 className="text-xl font-semibold text-blue-800 mb-3 flex items-center">
                {nutritionData[activeTab].hydration.icon}
                <span className="ml-2">Hydration Guide</span>
              </h3>
              <ul className="list-disc pl-10 space-y-2 text-blue-700">
                {nutritionData[activeTab].hydration.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
            <div className="bg-pink-50 p-5 rounded-xl border border-pink-100">
              <h3 className="text-xl font-semibold text-pink-800 mb-3">
                Recommended Supplements
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {nutritionData[activeTab].supplements.map((item, i) => (
                  <div key={i} className="flex items-start">
                    <span className="bg-pink-100 p-1 rounded-full mr-3 mt-1">
                      <FaHeart className="text-pink-600 text-xs" />
                    </span>
                    <span className="text-pink-800">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-100 mb-8">
          <h2 className="text-2xl font-bold text-purple-800 mb-4">
            {activeTab === "pregnancy"
              ? "Pregnancy Meal Plan"
              : "Cycle-Synced Meal Ideas"}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h3 className="font-semibold text-purple-700 mb-2">Breakfast</h3>
              <p className="text-sm text-purple-600">
                {activeTab === "pregnancy"
                  ? "Greek yogurt with berries, granola, and flaxseeds"
                  : "Menstrual: Oatmeal with blackstrap molasses and walnuts"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h3 className="font-semibold text-purple-700 mb-2">Lunch</h3>
              <p className="text-sm text-purple-600">
                {activeTab === "pregnancy"
                  ? "Quinoa salad with chickpeas, roasted veggies, and tahini dressing"
                  : "Ovulation: Salmon poke bowl with brown rice and avocado"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <h3 className="font-semibold text-purple-700 mb-2">Dinner</h3>
              <p className="text-sm text-purple-600">
                {activeTab === "pregnancy"
                  ? "Grilled chicken with sweet potato mash and steamed broccoli"
                  : "Luteal: Turkey chili with beans and dark leafy greens"}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-xl border border-purple-100 shadow-sm">
          <h2 className="text-2xl font-bold text-purple-800 mb-3">
            Professional Advice
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <h3 className="text-lg font-semibold text-green-700 mb-2 flex items-center">
                <FaHeart className="mr-2 text-green-600" />
                Best Practices
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-green-100 p-1 rounded-full mr-3">
                    <FaHeart className="text-green-600 text-xs" />
                  </span>
                  <span className="text-green-800">
                    {activeTab === "pregnancy"
                      ? "Eat every 2-3 hours to manage nausea and blood sugar"
                      : "Track symptoms to personalize your cycle nutrition"}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-green-100 p-1 rounded-full mr-3">
                    <FaHeart className="text-green-600 text-xs" />
                  </span>
                  <span className="text-green-800">
                    {activeTab === "pregnancy"
                      ? "Cook vegetables lightly to preserve nutrients"
                      : "Increase magnesium-rich foods 5 days before period"}
                  </span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-pink-700 mb-2 flex items-center">
                <FaTimes className="mr-2 text-pink-600" />
                Common Mistakes
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="bg-pink-100 p-1 rounded-full mr-3">
                    <FaTimes className="text-pink-600 text-xs" />
                  </span>
                  <span className="text-pink-800">
                    {activeTab === "pregnancy"
                      ? "Skipping protein at breakfast"
                      : "Ignoring iron needs during menstruation"}
                  </span>
                </li>
                <li className="flex items-start">
                  <span className="bg-pink-100 p-1 rounded-full mr-3">
                    <FaTimes className="text-pink-600 text-xs" />
                  </span>
                  <span className="text-pink-800">
                    {activeTab === "pregnancy"
                      ? "Overdoing sugary 'pregnancy cravings'"
                      : "Restricting calories during luteal phase"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        {/* Educational Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaUtensils className="mr-3 text-pink-500" />
              How to Start Nutrition Syncing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Track Your Needs
                </h3>
                <p className="text-gray-700 text-center">
                  Monitor cycle phases or pregnancy stages to align nutrition
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Plan Balanced Meals
                </h3>
                <p className="text-gray-700 text-center">
                  Include proteins, carbs, and healthy fats daily
                </p>
              </div>
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <div className="w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-green-700 mb-2">
                  Adjust Gradually
                </h3>
                <p className="text-gray-700 text-center">
                  Make small dietary changes based on body feedback
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaLeaf className="mr-3 text-purple-500" />
              Science of Nutrition for Women
            </h2>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Nutritional Impacts
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">
                    Hormone Support
                  </p>
                  <p className="text-gray-700">
                    Balanced diets stabilize estrogen and progesterone levels
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">
                    Energy Regulation
                  </p>
                  <p className="text-gray-700">
                    Nutrient timing reduces fatigue and cravings
                  </p>
                </div>
              </div>
              <p className="text-gray-700">
                Research shows tailored nutrition can improve women's health outcomes by up to 20% during key life stages.
              </p>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaHeart className="mr-3 text-green-500" />
              Benefits of Cycle-Synced Nutrition
            </h2>
            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 border border-green-200">
              <p className="text-gray-700 mb-4">
                Aligning diet with hormonal changes enhances overall well-being and vitality.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Reduced PMS symptoms through targeted nutrients</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Improved energy during pregnancy and cycles</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Better mood stability with balanced meals</span>
                </li>
                <li className="flex items-start">
                  <FaHeart className="text-green-500 mr-2 mt-0.5" />
                  <span>Enhanced recovery with proper hydration</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaTimes className="mr-3 text-red-500" />
              Debunking Nutrition Myths
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: All Cravings Are Bad
                </h3>
                <p className="text-gray-700">
                  Reality: Cravings often signal nutrient needs
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Supplements Replace Food
                </h3>
                <p className="text-gray-700">
                  Reality: Whole foods provide broader benefits
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: Low-Carb for All Phases
                </h3>
                <p className="text-gray-700">
                  Reality: Carbs support energy in specific cycle stages
                </p>
              </div>
              <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-red-700 mb-3">
                  Myth: One Diet Fits All
                </h3>
                <p className="text-gray-700">
                  Reality: Personalization is key for women's nutrition
                </p>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaSeedling className="mr-3 text-blue-500" />
              Practical Nutrition Tips
            </h2>
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200">
              <p className="text-gray-700 mb-4">
                Simple strategies to integrate hormone-friendly nutrition daily.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Meal Prep</h3>
                  <p className="text-gray-700 text-sm">
                    Plan meals weekly to ensure nutrient variety
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Snack Smart</h3>
                  <p className="text-gray-700 text-sm">
                    Choose nutrient-dense snacks to curb cravings
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Hydration Habit</h3>
                  <p className="text-gray-700 text-sm">
                    Keep a reusable water bottle handy
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-blue-600 mb-2">Listen to Body</h3>
                  <p className="text-gray-700 text-sm">
                    Adjust portions based on energy needs
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaPlay className="mr-3 text-yellow-500" />
              When to Seek Guidance
            </h2>
            <div className="bg-yellow-50 rounded-xl p-6 border border-yellow-200">
              <p className="text-gray-700 mb-4">
                Consult professionals for tailored nutrition plans in specific cases.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaPlay className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Persistent digestive issues</span>
                </li>
                <li className="flex items-start">
                  <FaPlay className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Unexplained fatigue or cravings</span>
                </li>
                <li className="flex items-start">
                  <FaPlay className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Pregnancy complications</span>
                </li>
                <li className="flex items-start">
                  <FaPlay className="text-yellow-500 mr-2 mt-0.5" />
                  <span>Severe menstrual symptoms</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Dietitians can optimize your women's nutrition approach.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NutritionTips;